import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Cable, Plus, Pause, Play, CircleHelp } from 'lucide-react';
import { Button } from 'components/ui/button';
import { useAuth } from 'context/auth';
import { mcpRequest, mcpQueryKey } from './McpAPI';
import { ConnectionSetup } from './McpSetup';
import { ConnectionList, McpActivity } from './McpActivity';
import ConnectionDialogs from './McpDialogs';
import './mcp.css';

export default function McpConnections() {
    const { auth } = useAuth();
    if (!auth?.admin)
        return (
            <main className="mcp-page">
                <p>Only Frontdoor administrators can manage AI connections.</p>
            </main>
        );
    return <Connections />;
}

function Connections() {
    const client = useQueryClient();
    const query = useQuery({
        queryKey: mcpQueryKey,
        queryFn: () => mcpRequest(),
        refetchInterval: 15_000,
    });
    const [error, setError] = useState('');
    const [statusPending, setStatusPending] = useState(false);
    const [creating, setCreating] = useState(false);
    const [revoking, setRevoking] = useState(null);
    const data = query.data;
    const enabled = Boolean(data?.enabled && data?.available);
    async function changeStatus() {
        setStatusPending(true);
        setError('');
        try {
            await mcpRequest('/status', 'PUT', { enabled: !data.enabled });
            await client.invalidateQueries({ queryKey: mcpQueryKey });
        } catch (err) {
            setError(err.message);
        } finally {
            setStatusPending(false);
        }
    }

    return (
        <main className="mcp-page">
            <div className="mcp-shell">
                <header className="mcp-header">
                    <div>
                        <div className="mcp-eyebrow">INTERNAL ADMIN / INTEGRATIONS</div>
                        <h1>
                            AI connections<span className="mcp-version">Read only</span>
                        </h1>
                        <p>Give your AI the context. Keep control of the access.</p>
                    </div>
                    <Button
                        onClick={() => {
                            setCreating(true);
                            setError('');
                        }}
                        disabled={!data?.available}
                    >
                        <Plus />
                        Create access key
                    </Button>
                </header>

                {(error || query.isError) && (
                    <div role="alert" className="mcp-error">
                        <span>{error || query.error.message}</span>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setError('');
                                query.refetch();
                            }}
                        >
                            Try again
                        </Button>
                    </div>
                )}
                {!data && query.isPending && (
                    <div className="mcp-loading" role="status" aria-label="Loading AI connections">
                        <div />
                        <div />
                        <div />
                    </div>
                )}
                {data && (
                    <>
                        <section
                            className={`mcp-control ${enabled ? 'is-live' : ''}`}
                            aria-label="Global MCP access"
                        >
                            <div className="mcp-control-icon">
                                <Cable size={25} />
                            </div>
                            <div className="mcp-control-copy">
                                <div className="mcp-status-heading">
                                    <span className="mcp-status-dot" />
                                    <h2>
                                        {enabled
                                            ? 'Your server is available'
                                            : 'All AI access is paused'}
                                    </h2>
                                </div>
                                <p>
                                    {enabled
                                        ? 'Approved connections can read Frontdoor data. You can stop every connection here.'
                                        : 'Bots cannot read Frontdoor data. Existing connections stay saved for when you resume.'}
                                </p>
                            </div>
                            <Button
                                className={data.enabled ? 'mcp-pause' : ''}
                                variant={data.enabled ? 'outline' : 'default'}
                                onClick={changeStatus}
                                disabled={statusPending || (!data.available && !data.enabled)}
                            >
                                {data.enabled ? <Pause /> : <Play />}
                                {statusPending
                                    ? 'Updating…'
                                    : data.enabled
                                      ? 'Pause all access'
                                      : 'Enable access'}
                            </Button>
                        </section>
                        {!data.available && (
                            <div className="mcp-notice" role="status">
                                <CircleHelp size={18} />
                                <span>
                                    The API server has not enabled MCP yet. Connection controls
                                    become available after server setup.
                                </span>
                            </div>
                        )}

                        <div className="mcp-layout">
                            <div className="mcp-primary">
                                <ConnectionList
                                    connections={data.connections}
                                    enabled={enabled}
                                    onRevoke={setRevoking}
                                />
                                <McpActivity activity={data.activity} />
                            </div>

                            <ConnectionSetup endpoint={data.endpoint} onError={setError} />
                        </div>
                    </>
                )}
            </div>

            <ConnectionDialogs
                creating={creating}
                setCreating={setCreating}
                revoking={revoking}
                setRevoking={setRevoking}
                onChanged={() => client.invalidateQueries({ queryKey: mcpQueryKey })}
            />
        </main>
    );
}
